// Combined DB/DC nondiscrimination testing for a cash balance + 401(k)/PS
// design, per 26 CFR 1.401(a)(4)-9 and §401(a)(26), using the shared versioned
// actuarial service and explicit aggregation contract:
//
//   AGGREGATION IS NEVER AUTOMATIC. The caller supplies explicit facts
//   (normalizeDbDcAggregationFacts): that the plans are being aggregated,
//   whether the combination is primarily DB in character or consists of
//   broadly available separate plans (either, when 'yes', lifts the gateway),
//   whether the NHCE DB equivalent-rate averaging rule is elected, and which
//   gateway route the documents use. Unknown facts keep the gateway required
//   and downgrade the result — they never lift a requirement.
//
//   GATEWAY (1.401(a)(4)-9(b)(2)(v)): each benefiting NHCE's DC allocation
//   rate plus DB equivalent allocation rate (averaged across benefiting NHCEs
//   only under the election) must reach the sliding requirement derived from
//   the highest HCE aggregate rate: one-third of H when H ≤ 15%, 5% when
//   15% < H ≤ 25%, then 5% plus 1pp per 5pp increment (or portion) above 25%.
//   An aggregate rate of 7.5% of §415(c)(3) compensation is deemed to satisfy
//   the gateway, so the effective requirement never exceeds 7.5%. Both the
//   uncapped sliding figure and the deemed cap are disclosed.
//
//   TESTING: aggregate NORMAL rates (CB plan-basis annuity + DC allocation
//   projected/annuitized on testing assumptions) and MOST VALUABLE rates (the
//   QJSA route, verified against a professional proposal's MVAR worksheet).
//   Rate groups must pass on BOTH; the classification component uses the
//   lesser of the safe-harbor midpoint and the plan ratio percentage; the
//   ABPT uses ABPT EBARs (deferrals and match included, never catch-ups).
//   §401(a)(26) minimum participation runs on a meaningful-benefit accrual
//   rate of at least 0.5% of pay.
//
//   PER-PLAN RESULTS: the DC component alone (through the audited
//   evaluateCrossTesting) and the DB component alone (accrual-rate screen),
//   so the outcome is explicit: passes-separately, passes-only-when-
//   aggregated, or fails. MODELED, never a formal test; not a substitute for
//   the plan's actuary.

import { evaluateCrossTesting } from './crossTesting.js'
import { classificationSafeHarborPercent, classificationUnsafePercent } from './coverage.js'
import { evaluateCashBalanceBenefit } from './cashBalanceBenefits.js'
import {
  actuarialBasisDescription,
  annuityFactor,
  DEFAULT_ACTUARIAL_SET,
  summarizeFactorsUsed,
} from './actuarialAssumptions.js'
import { CROSS_TEST_INTEREST_RATE } from './constants.js'

export const MEANINGFUL_ACCRUAL_RATE_PERCENT = 0.5
const roundBp = (percent) => Math.round(percent * 100) / 100

// §401(a)(26) minimum participation. A DB plan must benefit the LESSER of 50
// employees or the GREATER of 40% of all employees or 2 -- and a participant
// only counts when the accrual is MEANINGFUL, modeled here as an accrual rate
// of at least MEANINGFUL_ACCRUAL_RATE_PERCENT of pay. A one-person plan needs
// that one person. Callers pass the accrual rate for every NON-EXCLUDABLE
// participant, so the population decision stays with the caller.
//
// One implementation, shared by the combined DB/DC testing layer and the live
// workspace screen, so the two can never drift apart.
export function evaluateMinimumParticipation(accrualRatePercents = []) {
  const rates = Array.isArray(accrualRatePercents) ? accrualRatePercents : []
  const nonExcludableCount = rates.length
  const benefitingCount = rates
    .filter((rate) => Number(rate) >= MEANINGFUL_ACCRUAL_RATE_PERCENT).length
  const requiredBenefiting = nonExcludableCount === 1
    ? 1 : Math.min(50, Math.max(2, Math.ceil(0.4 * nonExcludableCount)))
  return {
    nonExcludableCount,
    benefitingCount,
    requiredBenefiting,
    passes: benefitingCount >= requiredBenefiting,
    meaningfulAccrualRatePercent: MEANINGFUL_ACCRUAL_RATE_PERCENT,
  }
}

// ---------------------------------------------------------------------------
// Aggregation facts (explicit, never inferred)
// ---------------------------------------------------------------------------

const TRI_STATE = ['yes', 'no', 'unknown']
const tri = (value) => (TRI_STATE.includes(value) ? value : 'unknown')

export function normalizeDbDcAggregationFacts(value = {}) {
  const source = value && typeof value === 'object' ? value : {}
  const primarilyDbCharacter = tri(source.primarilyDbCharacter)
  const broadlyAvailableSeparatePlans = tri(source.broadlyAvailableSeparatePlans)
  return {
    // The user (or an imported document) must say these plans are tested
    // together; two plans existing side by side never aggregates by itself.
    aggregate: source.aggregate === true,
    plansIncluded: Array.isArray(source.plansIncluded) && source.plansIncluded.length
      ? [...source.plansIncluded]
      : ['cash-balance', 'dc-profit-sharing'],
    primarilyDbCharacter,
    broadlyAvailableSeparatePlans,
    // The gateway applies unless one of the escapes is affirmatively 'yes'.
    gatewayRequired: !(primarilyDbCharacter === 'yes' || broadlyAvailableSeparatePlans === 'yes'),
    gatewayEscapesUnknown: primarilyDbCharacter === 'unknown' && broadlyAvailableSeparatePlans === 'unknown',
    nhceDbAveragingElection: source.nhceDbAveragingElection === true,
    gatewayMethod: source.gatewayMethod === 'seven-five-deemed' ? 'seven-five-deemed' : 'sliding',
  }
}

// ---------------------------------------------------------------------------
// Minimum aggregate allocation gateway requirement
// ---------------------------------------------------------------------------

export function requiredAggregateGatewayPercent(highestHceAggregatePercent) {
  const h = Number(highestHceAggregatePercent)
  if (!Number.isFinite(h) || h < 0) return null
  let sliding
  if (h <= 15) sliding = h / 3
  else if (h <= 25) sliding = 5
  else sliding = 5 + Math.ceil(roundBp(h - 25) / 5)
  const deemed = 7.5
  return {
    slidingPercent: roundBp(sliding),
    deemedPercent: deemed,
    // The deemed route caps what any NHCE can be required to receive.
    effectivePercent: roundBp(Math.min(sliding, deemed)),
    band: h <= 15 ? 'one-third' : h <= 25 ? 'five-percent' : 'sliding-above-25',
  }
}

// ---------------------------------------------------------------------------
// Combined evaluation
// ---------------------------------------------------------------------------

function rateGroupsFor(hces, nhces, key, thresholdPercent, abptPass) {
  return hces.map((hce) => {
    const threshold = hce[key] - 1e-6
    const hceCoverage = hces.filter((r) => r[key] >= threshold).length / hces.length * 100
    const nhceCoverage = nhces.filter((r) => r[key] >= threshold).length / nhces.length * 100
    const ratioPercent = hceCoverage > 0 ? (nhceCoverage / hceCoverage) * 100 : 100
    return {
      id: hce.id,
      ebar: hce[key],
      ratioPercent,
      thresholdPercent,
      passes: ratioPercent >= 70 || (ratioPercent >= thresholdPercent && abptPass),
      basis: ratioPercent >= 70 ? 'ratio' : (ratioPercent >= thresholdPercent && abptPass ? 'average-benefit' : 'none'),
    }
  })
}

// A DB or combined-benefit rate group is one intersection, not two parallel
// coverage groups: an employee belongs only when both the normal rate and the
// most-valuable rate equal or exceed the HCE's corresponding rates.
function combinedRateGroupsFor(hces, nhces, normalKey, mostValuableKey, thresholdPercent, abptPass) {
  return hces.map((hce) => {
    const normalThreshold = hce[normalKey] - 1e-6
    const mostValuableThreshold = hce[mostValuableKey] - 1e-6
    const belongs = (row) => row[normalKey] >= normalThreshold
      && row[mostValuableKey] >= mostValuableThreshold
    const hceCoverage = hces.filter(belongs).length / hces.length * 100
    const nhceCoverage = nhces.filter(belongs).length / nhces.length * 100
    const ratioPercent = hceCoverage > 0 ? (nhceCoverage / hceCoverage) * 100 : 100
    return {
      id: hce.id,
      ebar: hce[normalKey],
      mostValuableEbar: hce[mostValuableKey],
      ratioPercent,
      thresholdPercent,
      passes: ratioPercent >= 70 || (ratioPercent >= thresholdPercent && abptPass),
      basis: ratioPercent >= 70 ? 'ratio' : (ratioPercent >= thresholdPercent && abptPass ? 'average-benefit' : 'none'),
    }
  })
}

// `participants`: { id, isHCE, excludable, comp, gatewayComp, age, testingAge,
//   cbCredit, cbOpeningBalance?, dcAllocation (nonelective incl. SH + PS),
//   abptDcAllocation (+ deferrals and match, no catch-ups; defaults to
//   dcAllocation), deferral?, match? }
export function evaluateCombinedDbDc(participants = [], options = {}) {
  const facts = normalizeDbDcAggregationFacts(options.aggregationFacts)
  if (!facts.aggregate) {
    return {
      available: false,
      status: 'unavailable',
      reason: 'aggregation-not-confirmed',
      aggregationFacts: facts,
    }
  }
  const testingInterest = options.testingInterest ?? CROSS_TEST_INTEREST_RATE
  const interestCredit = options.interestCredit ?? 0.05
  const assumptionSet = options.assumptionSet ?? DEFAULT_ACTUARIAL_SET
  const factors = []
  const rows = []
  const missingMostValuableAges = new Set()
  for (const p of (Array.isArray(participants) ? participants : [])) {
    if (!p || p.excludable || !(Number(p.comp) > 0)) continue
    const testingAge = Number(p.testingAge) || 65
    const age = Number(p.age)
    const cb = evaluateCashBalanceBenefit({
      payCredit: p.cbCredit,
      openingBalance: p.cbOpeningBalance || 0,
      interestCredit,
      age,
      testingAge,
      comp: p.comp,
      testingInterest,
      planAprTable: options.planAprTable,
      testingAprTable: options.testingAprTable,
      assumptionSet,
      factors,
    })
    if (cb.status !== 'ok') {
      return { available: false, status: 'unavailable', reason: cb.reason, aggregationFacts: facts }
    }
    const testingApr = annuityFactor({ basis: 'testing', age: testingAge, customTable: options.testingAprTable, assumptionSet })
    const years = Math.max(0, testingAge - age)
    const projectDc = (allocation) => (Math.max(0, Number(allocation) || 0) * ((1 + testingInterest) ** years)) / testingApr.value
    const dcMonthly = projectDc(p.dcAllocation)
    const abptDcMonthly = projectDc(p.abptDcAllocation ?? p.dcAllocation)
    if (cb.mostValuableMonthly === null) missingMostValuableAges.add(Math.round(age))
    const comp = Number(p.comp)
    const gatewayComp = Number(p.gatewayComp) > 0 ? Number(p.gatewayComp) : comp
    const dcAllocation = Math.max(0, Number(p.dcAllocation) || 0)
    const abptDcAllocation = Math.max(0, Number(p.abptDcAllocation ?? p.dcAllocation) || 0)
    const cbCredit = Math.max(0, Number(p.cbCredit) || 0)
    rows.push({
      id: p.id ?? null,
      isHCE: Boolean(p.isHCE),
      comp,
      gatewayComp,
      age,
      testingAge,
      cbCredit,
      dcAllocation,
      abptDcAllocation,
      benefiting: dcAllocation > 0 || cbCredit > 0,
      benefitingUnderDb: cbCredit > 0,
      benefitingUnderDc: dcAllocation > 0,
      accrualRatePercent: cb.accrualRatePercent,
      dbEquivalentAllocationRatePercent: cb.equivalentAllocationRatePercent,
      hypotheticalBalance: cb.hypotheticalBalance,
      accruedMonthlyBenefit: cb.accruedMonthlyBenefit,
      // Gateway-side rates are measured on §415(c)(3) compensation.
      dbGatewayRatePercent: (cb.equivalentAllocation / gatewayComp) * 100,
      dcAllocationRatePercent: (dcAllocation / comp) * 100,
      dcGatewayRatePercent: (dcAllocation / gatewayComp) * 100,
      aggregateAllocationRatePercent: (cb.equivalentAllocation / gatewayComp) * 100
        + (dcAllocation / gatewayComp) * 100,
      aggregateEbar: ((cb.testingMonthlyBenefit + dcMonthly) * 12 / comp) * 100,
      mostValuableEbar: cb.mostValuableMonthly === null
        ? null : ((cb.mostValuableMonthly + dcMonthly) * 12 / comp) * 100,
      aggregateAbptEbar: ((cb.testingMonthlyBenefit + abptDcMonthly) * 12 / comp) * 100,
      dcEbar: (dcMonthly * 12 / comp) * 100,
    })
  }
  const hces = rows.filter((r) => r.isHCE)
  const nhces = rows.filter((r) => !r.isHCE)
  if (!hces.length || !nhces.length) {
    return {
      available: false,
      status: 'unavailable',
      reason: hces.length ? 'no-nhce' : 'no-hce',
      aggregationFacts: facts,
    }
  }

  // ---- §401(a)(26) minimum participation (meaningful-benefit screen) ----
  const participation = evaluateMinimumParticipation(rows.map((r) => r.accrualRatePercent))
  const { nonExcludableCount, benefitingCount, requiredBenefiting } = participation
  const minimumParticipationPasses = participation.passes

  // ---- Minimum aggregate allocation gateway ----
  // Benefiting NHCEs only (a non-benefiting NHCE is a coverage question,
  // surfaced via the count and ids). Rates at basis-point precision, matching
  // the professional worksheets this engine was verified against.
  const benefitingNhces = nhces.filter((r) => r.benefiting)
  const highestHceAggregateRate = hces.reduce((max, r) => Math.max(max, r.aggregateAllocationRatePercent), 0)
  const requirement = requiredAggregateGatewayPercent(highestHceAggregateRate)
  const operativeRequiredPercent = facts.gatewayMethod === 'seven-five-deemed'
    ? requirement.deemedPercent
    : requirement.effectivePercent
  const dbBenefitingNhces = benefitingNhces.filter((r) => r.cbCredit > 0)
  const averageNhceDbRatePercent = roundBp(dbBenefitingNhces.length
    ? dbBenefitingNhces.reduce((sum, r) => sum + r.dbGatewayRatePercent, 0) / dbBenefitingNhces.length
    : 0)
  const gatewayRows = benefitingNhces.map((r) => {
    const dbPercent = facts.nhceDbAveragingElection ? averageNhceDbRatePercent : roundBp(r.dbGatewayRatePercent)
    const gatewayRatePercent = roundBp(roundBp(r.dcGatewayRatePercent) + dbPercent)
    const shortfallPercent = Math.max(0, roundBp(operativeRequiredPercent - gatewayRatePercent))
    return {
      id: r.id,
      dcGatewayRatePercent: roundBp(r.dcGatewayRatePercent),
      dbGatewayRatePercent: dbPercent,
      dbRateAveraged: facts.nhceDbAveragingElection,
      gatewayRatePercent,
      requiredPercent: operativeRequiredPercent,
      shortfallPercent,
      shortfallDollars: (shortfallPercent / 100) * r.gatewayComp,
    }
  })
  const gatewayApplicable = facts.gatewayRequired
  const gatewaySatisfied = !gatewayApplicable
    || (operativeRequiredPercent !== null && gatewayRows.every((r) => r.shortfallPercent <= 0))
  const nonBenefitingNhceCount = nhces.length - benefitingNhces.length

  // ---- Classification threshold and plan ratio (lesser-of rule) ----
  const concentrationPercent = (nhces.length / rows.length) * 100
  const midpointPercent = (classificationSafeHarborPercent(concentrationPercent)
    + classificationUnsafePercent(concentrationPercent)) / 2
  const benefitingHceCount = hces.filter((r) => r.benefiting).length
  const benefitingNhceCount = benefitingNhces.length
  const hceBenefitingRate = (benefitingHceCount / hces.length) * 100
  const nhceBenefitingRate = (benefitingNhceCount / nhces.length) * 100
  const planRatioPercent = hceBenefitingRate > 0
    ? (nhceBenefitingRate / hceBenefitingRate) * 100
    : (nhceBenefitingRate > 0 ? 100 : 0)
  const classificationThresholdPercent = Math.min(midpointPercent, planRatioPercent)

  // ---- Aggregate rate groups (normal + most valuable) + ABPT ----
  const avg = (arr, key) => arr.reduce((sum, r) => sum + r[key], 0) / arr.length
  const nhceAvgEbar = avg(nhces, 'aggregateAbptEbar')
  const hceAvgEbar = avg(hces, 'aggregateAbptEbar')
  const abptRatio = hceAvgEbar > 0 ? (nhceAvgEbar / hceAvgEbar) * 100 : 100
  const abptPass = abptRatio >= 70
  const mostValuableAvailable = missingMostValuableAges.size === 0
  const rateGroups = mostValuableAvailable
    ? combinedRateGroupsFor(
        hces, nhces, 'aggregateEbar', 'mostValuableEbar',
        classificationThresholdPercent, abptPass)
    : []
  const allRateGroupsPass = mostValuableAvailable && rateGroups.every((g) => g.passes)
  // Kept as an alias for existing worksheets/UI, but it is intentionally the
  // same statutory intersection rather than a second independent group.
  const mostValuableRateGroups = rateGroups.map((group) => ({ ...group }))
  const allMostValuablePass = allRateGroupsPass
  const mostValuable = {
    available: mostValuableAvailable,
    reason: mostValuableAvailable ? null : 'missing-joint-apr-for-age',
    missingAges: [...missingMostValuableAges].sort((a, b) => a - b),
    rateGroups: mostValuableRateGroups,
    allRateGroupsPass: allMostValuablePass,
  }

  // ---- Per-plan results (screens): DC alone and DB alone ----
  const dcSeparate = evaluateCrossTesting(rows.map((r) => ({
    id: r.id,
    isHCE: r.isHCE,
    comp: r.comp,
    gatewayComp: r.gatewayComp,
    age: r.age,
    testingAge: r.testingAge,
    allocation: r.dcAllocation,
    // DB value is excluded for the separate DC screen, while elective
    // deferrals and match remain in the average-benefit percentage test.
    abptAllocation: r.abptDcAllocation,
  })), { planYear: options.planYear })
  const dbAbptRatio = (() => {
    const nhceDb = avg(nhces, 'accrualRatePercent')
    const hceDb = avg(hces, 'accrualRatePercent')
    return hceDb > 0 ? (nhceDb / hceDb) * 100 : 100
  })()
  const dbRateGroups = rateGroupsFor(hces, nhces, 'accrualRatePercent', classificationThresholdPercent, dbAbptRatio >= 70)
  const dbSeparate = {
    screen: true,
    rateGroups: dbRateGroups,
    allRateGroupsPass: dbRateGroups.every((g) => g.passes),
    abptRatio: dbAbptRatio,
    abptPass: dbAbptRatio >= 70,
    passes: dbRateGroups.every((g) => g.passes),
  }
  const dcSeparatePasses = dcSeparate.available && dcSeparate.gatewaySatisfied && dcSeparate.generalTestPasses
  const combinedPasses = minimumParticipationPasses && gatewaySatisfied
    && allRateGroupsPass && mostValuableAvailable && allMostValuablePass
  const aggregationOutcome = dcSeparatePasses && dbSeparate.passes
    ? 'passes-separately'
    : combinedPasses ? 'passes-only-when-aggregated' : 'fails'

  const generalTestPasses = allRateGroupsPass && mostValuableAvailable && allMostValuablePass
  const status = !minimumParticipationPasses ? 'fails-401a26'
    : !gatewaySatisfied ? 'gateway-not-met'
      : !mostValuableAvailable ? 'passes-normal-only'
        : !generalTestPasses ? 'fails' : 'passes'

  const factorsUsed = summarizeFactorsUsed(factors)
  const nonVerified = factorsUsed.filter((f) => f.provenance !== 'verified' && f.provenance !== 'custom')

  return {
    available: true,
    status,
    aggregationFacts: facts,
    rows,
    minimumParticipation: {
      nonExcludableCount,
      benefitingCount,
      requiredBenefiting,
      passes: minimumParticipationPasses,
      meaningfulAccrualRatePercent: MEANINGFUL_ACCRUAL_RATE_PERCENT,
    },
    highestHceAggregateRate,
    gatewayRequirement: requirement,
    gatewayMethod: facts.gatewayMethod,
    gatewayApplicable,
    requiredGatewayPercent: gatewayApplicable ? operativeRequiredPercent : null,
    averageNhceDbRatePercent,
    nhceDbAveragingElection: facts.nhceDbAveragingElection,
    gatewayRows,
    gatewaySatisfied,
    nonBenefitingNhceCount,
    nonBenefitingNhceIds: nhces.filter((r) => !r.benefiting).map((r) => r.id),
    concentrationPercent,
    midpointPercent,
    planRatioPercent,
    classificationThresholdPercent,
    nhceAvgEbar,
    hceAvgEbar,
    abptRatio,
    abptPass,
    rateGroups,
    allRateGroupsPass,
    mostValuable,
    generalTestPasses,
    perPlan: {
      dcSeparate,
      dcSeparatePasses,
      dbSeparate,
    },
    aggregationOutcome,
    assumptions: {
      summary: [
        `Testing basis: ${actuarialBasisDescription('testing', assumptionSet)}; plan basis: ${actuarialBasisDescription('plan', assumptionSet)}.`,
        `Interest: ${(interestCredit * 100).toFixed(1)}% plan interest credit, ${(testingInterest * 100).toFixed(1)}% standard testing rate; no pre-retirement mortality.`,
        'Most-valuable rates use the QJSA route: account → immediate J&S at attained age (plan basis) → testing value → accumulated and annuitized at testing age.',
        facts.nhceDbAveragingElection
          ? 'Gateway uses the elected NHCE cash-balance averaging rule; rates compared at basis-point precision.'
          : 'Gateway uses each NHCE’s own cash-balance equivalent rate (averaging not elected); rates compared at basis-point precision.',
        'ABPT counts elective deferrals and match; rate groups count nonelective money only; catch-ups never count.',
      ],
      factorsUsed,
      estimated: nonVerified,
      // Honest scope: DB-side aspects this model does not evaluate. A passing
      // result here is conditional on a TPA/actuary confirming these.
      limitations: [
        'Measurement period is the current year only; accrued-to-date and projected methods, and fresh-start alternatives, are not modeled.',
        'Most-valuable rates use only the QJSA route; early-retirement subsidies, plan windows, and QSUPPs are not valued.',
        'The DB safety-valve (facts-and-circumstances relief for a small number of HCEs) is not applied.',
        'Whether the combination is primarily DB in character or consists of broadly available separate plans is taken from the supplied facts, never determined here.',
        'Compensation is taken as entered; the §414(s) compensation-ratio test for a non-safe-harbor definition is not performed.',
      ],
    },
  }
}
